import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Chip,
  Paper,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Collapse,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  IconButton,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import OrderActionsDialog from '../components/orders/OrderActionsDialog';
import {
  buildOrdersSearchSnapshot,
  saveOrdersSearchSnapshotToSession,
  loadOrdersSearchSnapshotFromSession,
  clearOrdersSearchSnapshotFromSession,
} from '../utils/ordersSearchPersist';
import ZellePaymentsPage from './ZellePaymentsPage';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

/**
 * Reorder only the current page of results (client-side). Server always returns pages in date desc
 * so pagination stays stable; column headers do not change which orders are on each page.
 */
function sortOrdersPage(rows, sortBy, sortDir) {
  const dir = sortDir === 'asc' ? 1 : -1;
  const copy = [...rows];
  copy.sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'id':
        cmp = (Number(a.id) || 0) - (Number(b.id) || 0);
        break;
      case 'date': {
        const ta = new Date(a.date_created_gmt || 0).getTime();
        const tb = new Date(b.date_created_gmt || 0).getTime();
        cmp = ta - tb;
        break;
      }
      case 'devotee': {
        const da = `${a.billing_first_name || ''} ${a.billing_last_name || ''}`.trim() || (a.billing_email || '');
        const db = `${b.billing_first_name || ''} ${b.billing_last_name || ''}`.trim() || (b.billing_email || '');
        cmp = da.localeCompare(db, undefined, { sensitivity: 'base' });
        break;
      }
      case 'status':
        cmp = String(a.status || '').localeCompare(String(b.status || ''), undefined, { sensitivity: 'base' });
        break;
      case 'total':
        cmp = (Number(a.total_num) || 0) - (Number(b.total_num) || 0);
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return dir * cmp;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
  return copy;
}

const OrdersSearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Filters
  // New date range picker state
  const [rangePreset, setRangePreset] = React.useState('all');
  const [startDate, setStartDate] = React.useState(''); // yyyy-mm-dd
  const [endDate, setEndDate] = React.useState('');   // yyyy-mm-dd
  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [paymentMethods, setPaymentMethods] = React.useState([]); // selected list
  const [minTotal, setMinTotal] = React.useState('');
  const [maxTotal, setMaxTotal] = React.useState('');
  const [productId, setProductId] = React.useState(''); // legacy single; kept for chip rendering if needed
  const [variationId, setVariationId] = React.useState(''); // legacy single
  const [channel, setChannel] = React.useState('');
  const [staff, setStaff] = React.useState('');
  const [guest, setGuest] = React.useState(''); // guest search text
  const [notes, setNotes] = React.useState(''); // customer notes / memo search
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(25);
  const [sortBy, setSortBy] = React.useState('date');
  const [sortDir, setSortDir] = React.useState('desc');

  // Results
  const [orders, setOrders] = React.useState([]);
  const [meta, setMeta] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Facets and Categories
  const [paymentMethodsFacet, setPaymentMethodsFacet] = React.useState([]);
  const [productOptions, setProductOptions] = React.useState([]);
  const [variationOptions, setVariationOptions] = React.useState([]);
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [selectedVariations, setSelectedVariations] = React.useState([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [loadingVariations, setLoadingVariations] = React.useState(false);
  const [guestOptions, setGuestOptions] = React.useState([]);
  const [loadingGuests, setLoadingGuests] = React.useState(false);

  // Dialog
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [pledgeOrdersOpen, setPledgeOrdersOpen] = React.useState(false);
  const [pledgeOrders, setPledgeOrders] = React.useState([]);
  const [loadingPledgeOrders, setLoadingPledgeOrders] = React.useState(false);

  // Left nav: which view to show on the right (search = orders table, zelle = Zelle orders)
  const [ordersView, setOrdersView] = React.useState('search');

  // Convert local yyyy-mm-dd to UTC ISO range boundaries
  const toUtcIsoFromLocal = (ymd, endOfDay = false) => {
    if (!ymd) return '';
    const [y, m, d] = String(ymd).split('-').map(n => parseInt(n, 10));
    if (!y || !m || !d) return '';
    const dt = new Date(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
    return dt.toISOString();
  };

  const handleSearch = async (opts = {}, overrides = {}) => {
    try {
      setLoading(true);
      setError('');
      // sort_by / sort_dir may be passed in opts (e.g. handleSort) or overrides (e.g. handleReset); not only overrides.
      const params = {
        page: opts.page || page,
        per_page: overrides.perPage ?? perPage,
        sort_by: overrides.sort_by ?? opts.sort_by ?? sortBy,
        sort_dir: overrides.sort_dir ?? opts.sort_dir ?? sortDir,
      };
      const filters = {
        q,
        status,
        paymentMethods,
        minTotal,
        maxTotal,
        selectedProducts,
        selectedVariations,
        channel,
        staff,
        guest,
        notes,
        startDate,
        endDate,
        ...overrides,
      };
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (Array.isArray(filters.paymentMethods) && filters.paymentMethods.length) params.payment_methods = filters.paymentMethods.join(',');
      if (filters.minTotal) params.min_total = filters.minTotal;
      if (filters.maxTotal) params.max_total = filters.maxTotal;
      const productIds = Array.isArray(filters.selectedProducts) ? filters.selectedProducts.map(p => p.id) : [];
      const variationIds = Array.isArray(filters.selectedVariations) ? filters.selectedVariations.map(v => v.id) : [];
      if (productIds.length) params.product_ids = productIds.join(',');
      if (variationIds.length) params.variation_ids = variationIds.join(',');
      if (filters.channel) params.channel = filters.channel;
      if (filters.staff) params.staff = filters.staff;
      if (filters.guest) params.guest = filters.guest;
      if (filters.notes && String(filters.notes).trim()) params.notes = String(filters.notes).trim();
      const sYmd = opts.startYmd ?? filters.startDate;
      const eYmd = opts.endYmd ?? filters.endDate;
      if (sYmd) params.start = toUtcIsoFromLocal(sYmd, false);
      if (eYmd) params.end = toUtcIsoFromLocal(eYmd, true);
      const res = await get('/ldb/orders/search', params);
      const items = Array.isArray(res?.data) ? res.data : [];
      setOrders(items);
      const m = res?.meta || null;
      setMeta(m);

      saveOrdersSearchSnapshotToSession(buildOrdersSearchSnapshot({
        rangePreset,
        startDate,
        endDate,
        q: filters.q ?? '',
        status: filters.status ?? '',
        paymentMethods: filters.paymentMethods ?? [],
        minTotal: filters.minTotal ?? '',
        maxTotal: filters.maxTotal ?? '',
        channel: filters.channel ?? '',
        staff: filters.staff ?? '',
        guest: filters.guest ?? '',
        notes: filters.notes ?? '',
        selectedProducts: filters.selectedProducts ?? [],
        selectedVariations: filters.selectedVariations ?? [],
        page: opts.page ?? page,
        perPage: overrides.perPage ?? perPage,
        sortBy: overrides.sort_by ?? opts.sort_by ?? sortBy,
        sortDir: overrides.sort_dir ?? opts.sort_dir ?? sortDir,
        ordersView,
      }));
    } catch (e) {
      setError(e?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const goToOrderEdit = React.useCallback((o) => {
    const id = Number(o?.id);
    if (!id) return;
    saveOrdersSearchSnapshotToSession(buildOrdersSearchSnapshot({
      rangePreset,
      startDate,
      endDate,
      q,
      status,
      paymentMethods,
      minTotal,
      maxTotal,
      channel,
      staff,
      guest,
      notes,
      selectedProducts,
      selectedVariations,
      page,
      perPage,
      sortBy,
      sortDir,
      ordersView,
    }));
    navigate(`/orders/${id}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
  }, [navigate, location.pathname, location.search, rangePreset, startDate, endDate, q, status, paymentMethods, minTotal, maxTotal, channel, staff, guest, notes, selectedProducts, selectedVariations, page, perPage, sortBy, sortDir, ordersView]);

  const handleReset = () => {
    clearOrdersSearchSnapshotFromSession();
    const { startYmd, endYmd } = applyPreset('all');
    setGuest('');
    setNotes('');
    setQ('');
    setStatus('');
    setPaymentMethods([]);
    setMinTotal('');
    setMaxTotal('');
    setProductId('');
    setVariationId('');
    setChannel('');
    setStaff('');
    setSelectedProducts([]);
    setSelectedVariations([]);
    setPage(1);
    setPerPage(25);
    setSortBy('date');
    setSortDir('desc');
    setMeta(null);
    setError('');
    handleSearch(
      { page: 1, startYmd, endYmd },
      {
        q: '',
        status: '',
        paymentMethods: [],
        minTotal: '',
        maxTotal: '',
        selectedProducts: [],
        selectedVariations: [],
        channel: '',
        staff: '',
        guest: '',
        notes: '',
        perPage: 25,
      }
    );
  };

  const handleSort = (column) => {
    const nextDir =
      sortBy === column
        ? (sortDir === 'asc' ? 'desc' : 'asc')
        : (column === 'devotee' || column === 'status' ? 'asc' : 'desc');
    setSortBy(column);
    setSortDir(nextDir);
    // Sort is client-only but should survive return from order edit
    saveOrdersSearchSnapshotToSession(buildOrdersSearchSnapshot({
      rangePreset,
      startDate,
      endDate,
      q,
      status,
      paymentMethods,
      minTotal,
      maxTotal,
      channel,
      staff,
      guest,
      notes,
      selectedProducts,
      selectedVariations,
      page,
      perPage,
      sortBy: column,
      sortDir: nextDir,
      ordersView,
    }));
  };

  const displayOrders = React.useMemo(
    () => sortOrdersPage(orders, sortBy, sortDir),
    [orders, sortBy, sortDir]
  );

  /** Shared styles so sortable headers read as clickable (MUI hides arrows when inactive). */
  const sortHeaderSx = (key) => ({
    userSelect: 'none',
    borderRadius: 0.5,
    px: 0.25,
    mx: -0.25,
    py: 0.25,
    transition: 'background-color 0.15s ease',
    '&:hover': { bgcolor: 'action.hover' },
    '& .MuiTableSortLabel-icon': {
      fontSize: 18,
      opacity: `${sortBy === key ? 1 : 0.55} !important`,
    },
    ...(sortBy === key ? { color: 'primary.main' } : {}),
  });

  const fmt = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleString(undefined, {
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch { return d; }
  };

  // Date-only label (no time) for the Date Range button, using LOCAL date construction to avoid TZ shift
  const fmtDateOnly = (ymd) => {
    try {
      if (!ymd) return '';
      const [y, m, d] = String(ymd).split('-').map(n => parseInt(n, 10));
      if (!y || !m || !d) return ymd;
      const dt = new Date(y, m - 1, d); // local date, midnight local time
      return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch { return ymd; }
  };

  const periodLabel = React.useMemo(() => {
    const s = startDate ? fmtDateOnly(startDate) : '';
    const e = endDate ? fmtDateOnly(endDate) : '';
    if (s && e) return `${s} to ${e}`;
    if (s) return `${s} to ...`;
    if (e) return `... to ${e}`;
    // default display for preset
    switch (rangePreset) {
      case 'all': return 'All time';
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last_7': return 'Last 7 days';
      case 'last_30': return 'Last 30 days';
      case 'last_90': return 'Last 90 days';
      case 'week_to_date': return 'Week to date';
      case 'month_to_date': return 'Month to date';
      case 'custom': return 'Custom';
      default: return 'Last 12 months';
    }
  }, [startDate, endDate, rangePreset]);

  // Helpers to compute preset dates (yyyy-mm-dd)
  const toYMD = (d) => {
    const yy = d.getFullYear();
    const mm = String(d.getMonth()+1).padStart(2,'0');
    const dd = String(d.getDate()).padStart(2,'0');
    return `${yy}-${mm}-${dd}`;
  };
  const applyPreset = (preset) => {
    setRangePreset(preset);
    if (preset === 'custom') {
      setStartDate('');
      setEndDate('');
      return { startYmd: '', endYmd: '' };
    }
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);
    let startYmd = '';
    let endYmd = '';
    switch (preset) {
      case 'today':
        startYmd = toYMD(now); endYmd = toYMD(now); break;
      case 'yesterday': {
        const y = new Date(now); y.setDate(y.getDate()-1);
        startYmd = toYMD(y); endYmd = toYMD(y); break; }
      case 'last_7':
        start.setDate(now.getDate()-6); startYmd = toYMD(start); endYmd = toYMD(end); break;
      case 'last_30':
        start.setDate(now.getDate()-29); startYmd = toYMD(start); endYmd = toYMD(end); break;
      case 'last_90':
        start.setDate(now.getDate()-89); startYmd = toYMD(start); endYmd = toYMD(end); break;
      case 'week_to_date': {
        const wd = now.getDay(); // 0 Sun
        const diff = (wd+6)%7; // Monday start
        start.setDate(now.getDate()-diff);
        startYmd = toYMD(start); endYmd = toYMD(end); break; }
      case 'month_to_date':
        start.setDate(1); startYmd = toYMD(start); endYmd = toYMD(end); break;
      case 'all':
        startYmd = ''; endYmd = ''; break;
      default: // last_12_months
        start.setFullYear(now.getFullYear()-1);
        startYmd = toYMD(start); endYmd = toYMD(end); break;
    }
    // Update state for UI and return the computed ymds
    if (startYmd) setStartDate(startYmd);
    if (endYmd) setEndDate(endYmd);
    return { startYmd, endYmd };
  };

  // Fetch facets and categories on mount
  React.useEffect(() => {
    (async () => {
      try {
        const f = await get('/ldb/facets/payment-methods');
        const pm = Array.isArray(f?.payment_methods) ? f.payment_methods : [];
        setPaymentMethodsFacet(pm);
      } catch {}
      // Category filter removed
      // Prefetch some products for initial dropdown options
      try { await fetchProducts(''); } catch {}
    })();
  }, []);

  // Initial load: restore saved filters when returning from order edit (or Refresh), otherwise default search
  React.useEffect(() => {
    const savedFlag = !!location.state?.orderEditSaved;
    const snap = loadOrdersSearchSnapshotFromSession();
    if (snap) {
      setRangePreset(snap.rangePreset ?? 'all');
      setStartDate(snap.startDate ?? '');
      setEndDate(snap.endDate ?? '');
      setQ(snap.q ?? '');
      setStatus(snap.status ?? '');
      setPaymentMethods(Array.isArray(snap.paymentMethods) ? snap.paymentMethods : []);
      setMinTotal(snap.minTotal ?? '');
      setMaxTotal(snap.maxTotal ?? '');
      setChannel(snap.channel ?? '');
      setStaff(snap.staff ?? '');
      setGuest(snap.guest ?? '');
      setNotes(snap.notes ?? '');
      setSelectedProducts(Array.isArray(snap.selectedProducts) ? snap.selectedProducts : []);
      setSelectedVariations(Array.isArray(snap.selectedVariations) ? snap.selectedVariations : []);
      setPage(snap.page ?? 1);
      setPerPage(snap.perPage ?? 25);
      setSortBy(snap.sortBy ?? 'date');
      setSortDir(snap.sortDir ?? 'desc');
      setOrdersView(snap.ordersView === 'zelle' ? 'zelle' : 'search');
      void handleSearch(
        {
          page: snap.page ?? 1,
          startYmd: snap.startDate,
          endYmd: snap.endDate,
          sort_by: snap.sortBy,
          sort_dir: snap.sortDir,
        },
        {
          q: snap.q ?? '',
          status: snap.status ?? '',
          paymentMethods: Array.isArray(snap.paymentMethods) ? snap.paymentMethods : [],
          minTotal: snap.minTotal ?? '',
          maxTotal: snap.maxTotal ?? '',
          selectedProducts: Array.isArray(snap.selectedProducts) ? snap.selectedProducts : [],
          selectedVariations: Array.isArray(snap.selectedVariations) ? snap.selectedVariations : [],
          channel: snap.channel ?? '',
          staff: snap.staff ?? '',
          guest: snap.guest ?? '',
          notes: snap.notes ?? '',
          perPage: snap.perPage ?? 25,
        }
      );
    } else {
      const { startYmd, endYmd } = applyPreset('all');
      void handleSearch({ startYmd, endYmd }, {});
    }
    if (savedFlag) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, []);

  // Load products autocomplete options on demand
  const fetchProducts = React.useMemo(() => async (term) => {
    try {
      setLoadingProducts(true);
      const res = await get('/ldb/facets/products', { q: term || '', limit: 100 });
      setProductOptions(Array.isArray(res?.products) ? res.products : []);
    } catch (e) {
      console.error('[Facets][Products] load failed', e?.message || e);
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Load variations when products selection changes
  React.useEffect(() => {
    (async () => {
      const ids = selectedProducts.map(p => p.id).join(',');
      if (!ids) { setVariationOptions([]); setSelectedVariations([]); return; }
      try {
        setLoadingVariations(true);
        const res = await get('/ldb/facets/variations', { product_id: ids, limit: 300 });
        setVariationOptions(Array.isArray(res?.variations) ? res.variations : []);
      } catch (e) {
        console.error('[Facets][Variations] load failed', e?.message || e);
        setVariationOptions([]);
      } finally {
        setLoadingVariations(false);
      }
    })();
  }, [selectedProducts]);

  const toggleExpandCat = (id) => {
    setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const CategoryNode = ({ node, depth = 0 }) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const selected = String(categoryId) === String(node.id);
    return (
      <>
        <ListItemButton dense sx={{ pl: 1 + depth * 2 }} onClick={() => hasChildren ? toggleExpandCat(node.id) : setCategoryId(selected ? '' : String(node.id))}>
          <Checkbox size="small" edge="start" tabIndex={-1} disableRipple checked={selected} onChange={() => setCategoryId(selected ? '' : String(node.id))} />
          <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`${node.name} ${node.count ? `(${node.count})` : ''}`} />
        </ListItemButton>
        {hasChildren && (
          <Collapse in={!!expandedCats[node.id]} timeout="auto" unmountOnExit>
            <List disablePadding>
              {node.children.map(child => (
                <CategoryNode key={child.id} node={child} depth={depth + 1} />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  const ActiveChips = () => {
    const chips = [];
    if (q) chips.push({ label: `Query: ${q}`, onDelete: () => setQ('') });
    if (status) chips.push({ label: `Status: ${status}`, onDelete: () => setStatus('') });
    if (paymentMethods.length) paymentMethods.forEach(pm => chips.push({ label: `Payment: ${pm}`, onDelete: () => setPaymentMethods(paymentMethods.filter(x => x!==pm)) }));
    // Category filter removed
    if (minTotal) chips.push({ label: `Min: ${minTotal}`, onDelete: () => setMinTotal('') });
    if (maxTotal) chips.push({ label: `Max: ${maxTotal}`, onDelete: () => setMaxTotal('') });
    if (selectedProducts.length) selectedProducts.forEach(p => chips.push({ label: `Product: ${p.name || p.id}`, onDelete: () => setSelectedProducts(selectedProducts.filter(x => x.id!==p.id)) }));
    if (selectedVariations.length) selectedVariations.forEach(v => chips.push({ label: `Var: ${v.sku || v.id}`, onDelete: () => setSelectedVariations(selectedVariations.filter(x => x.id!==v.id)) }));
    if (channel) chips.push({ label: `Channel: ${channel}`, onDelete: () => setChannel('') });
    if (staff) chips.push({ label: `Staff: ${staff}`, onDelete: () => setStaff('') });
    if (guest) chips.push({ label: `Donor: ${guest}`, onDelete: () => setGuest('') });
    if (notes) chips.push({ label: `Notes: ${notes}`, onDelete: () => setNotes('') });
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {chips.map((c, idx) => (
          <Chip key={idx} label={c.label} onDelete={c.onDelete} />
        ))}
      </Stack>
    );
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 0 }}>Search Orders</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/post-payment')}
        >
          Add Order
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ flexWrap: 'nowrap' }}>
        <Grid item sx={{ flexBasis: 272, maxWidth: 306, minWidth: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Filters box - self-contained */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Filters</Typography>
            <Divider sx={{ mb: 1 }} />

            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                label="Date Range"
                value={rangePreset}
                onChange={(e) => {
                  const value = e.target.value;
                  const { startYmd, endYmd } = applyPreset(value);
                  if (value !== 'custom') {
                    handleSearch({ startYmd, endYmd });
                  }
                }}
              >
                {[
                  { k: 'all', t: 'All' },
                  { k: 'last_12_months', t: 'Last 12 months' },
                  { k: 'today', t: 'Today' },
                  { k: 'yesterday', t: 'Yesterday' },
                  { k: 'last_7', t: 'Last 7 days' },
                  { k: 'last_30', t: 'Last 30 days' },
                  { k: 'last_90', t: 'Last 90 days' },
                  { k: 'week_to_date', t: 'Week to date' },
                  { k: 'month_to_date', t: 'Month to date' },
                  { k: 'custom', t: 'Custom' },
                ].map(opt => (
                  <MenuItem key={opt.k} value={opt.k}>
                    {opt.t}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{periodLabel || ' '}</FormHelperText>
            </FormControl>

            {rangePreset === 'custom' && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    type="date"
                    label="Start"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!startDate || !endDate}
                    onClick={() => handleSearch({ startYmd: startDate, endYmd: endDate })}
                  >
                    Apply
                  </Button>
                </Stack>
              </Stack>
            )}

            <Autocomplete
              freeSolo
              size="small"
              options={guestOptions}
              getOptionLabel={(o) => {
                if (!o) return '';
                if (typeof o === 'string') return o;
                const name = `${o.first_name || ''} ${o.last_name || ''}`.trim();
                const spouse = o.spouse_name ? ` • ${o.spouse_name}` : '';
                const email = o.email ? ` — ${o.email}` : '';
                return (name || o.email || '').trim() + spouse + email;
              }}
              filterOptions={(x) => x} // server-side filtering
              loading={loadingGuests}
              value={guest}
              onChange={(_, val) => {
                if (!val) { setGuest(''); return; }
                if (typeof val === 'string') { setGuest(val); return; }
                // Prefer email if present for precise match
                setGuest((val.email || `${val.first_name || ''} ${val.last_name || ''}`).trim());
                handleSearch();
              }}
              onInputChange={(_, input) => {
                if (typeof input !== 'string') return;
                setGuest(input);
                const q = input.trim();
                if (q.length < 3) { setGuestOptions([]); return; }
                (async () => {
                  try {
                    setLoadingGuests(true);
                    const res = await get('/ldb/facets/guests', { q });
                    setGuestOptions(Array.isArray(res?.guests) ? res.guests : []);
                  } catch (e) {
                    setGuestOptions([]);
                  } finally {
                    setLoadingGuests(false);
                  }
                })();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Donor (name or email)"
                  placeholder="Type 3+ characters"
                  fullWidth
                  size="small"
                />
              )}
              sx={{ mb: 1 }}
            />

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Products</Typography>
            <Autocomplete
              multiple
              size="small"
              options={productOptions}
              isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
              getOptionLabel={(o) => {
                if (!o || typeof o !== 'object') return String(o || '');
                const name = o.name || `#${o.id}`;
                const sku = o.sku ? ` • ${o.sku}` : '';
                return `${name}${sku}`;
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name || `#${option.id}`}{option.sku ? ` • ${option.sku}` : ''}
                </li>
              )}
              value={selectedProducts}
              onChange={(_, v) => setSelectedProducts(v)}
              onInputChange={(_, v) => fetchProducts(v)}
              loading={loadingProducts}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search products" InputProps={{ ...params.InputProps, endAdornment: (<>{loadingProducts ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
              sx={{ mb: 1 }}
            />

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Variations</Typography>
            <Autocomplete
              multiple
              size="small"
              options={variationOptions}
              isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
              getOptionLabel={(o) => {
                if (!o || typeof o !== 'object') return String(o || '');
                // Try to build label from attributes first
                const attrs = Array.isArray(o.attributes) ? o.attributes : [];
                if (attrs.length > 0) {
                  const label = attrs.map(a => {
                    const name = (a.name || a.attribute || '').toString();
                    const val = (a.option || a.value || '').toString();
                    return name && val ? `${name}: ${val}` : (val || name);
                  }).filter(Boolean).join(', ');
                  if (label) return label;
                }
                // Fallback to SKU or ID
                const sku = o.sku && String(o.sku).trim();
                return sku && sku.length > 0 ? sku : `#${o.id}`;
              }}
              renderOption={(props, option) => {
                // Build label from attributes
                const attrs = Array.isArray(option.attributes) ? option.attributes : [];
                let label = '';
                if (attrs.length > 0) {
                  label = attrs.map(a => {
                    const name = (a.name || a.attribute || '').toString();
                    const val = (a.option || a.value || '').toString();
                    return name && val ? `${name}: ${val}` : (val || name);
                  }).filter(Boolean).join(', ');
                }
                if (!label) {
                  label = (option.sku && String(option.sku).trim()) || `#${option.id}`;
                }
                return (
                  <li {...props} key={option.id}>
                    {label}
                    {typeof option.price_num === 'number' ? ` — $${option.price_num.toFixed(2)}` : ''}
                  </li>
                );
              }}
              value={selectedVariations}
              onChange={(_, v) => setSelectedVariations(v)}
              loading={loadingVariations}
              renderInput={(params) => (
                <TextField {...params} placeholder={selectedProducts.length ? 'Select variations' : 'Select products first'} InputProps={{ ...params.InputProps, endAdornment: (<>{loadingVariations ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
              sx={{ mb: 1 }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="pledge-pending">Pledge Pending</MenuItem>
                <MenuItem value="pledge-completed">Pledge Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Payment Methods</Typography>
            <Autocomplete
              multiple
              size="small"
              options={paymentMethodsFacet.map(pm => pm.payment_method || '')}
              value={paymentMethods}
              onChange={(_, v) => setPaymentMethods(v)}
              renderInput={(params) => <TextField {...params} placeholder="Select payment methods" />}
              sx={{ mb: 1 }}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField label="Min Total" value={minTotal} onChange={(e) => setMinTotal(e.target.value)} fullWidth size="small" />
              <TextField label="Max Total" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} fullWidth size="small" />
            </Stack>

            <TextField label="Order # / Amount" value={q} onChange={(e) => setQ(e.target.value)} fullWidth size="small" sx={{ mt: 1, mb: 1 }} />

            <TextField label="Notes (memo / customer note)" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth size="small" placeholder="Search in order notes" sx={{ mb: 1 }} />

            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleReset} size="small">Reset</Button>
              <Button variant="contained" onClick={handleSearch} size="small">Search</Button>
            </Stack>
          </Paper>

          {/* Nav box - strictly outside and below the Filters box */}
          <Paper variant="outlined" sx={{ mt: 3, overflow: 'hidden', flexShrink: 0, borderTop: '2px solid', borderTopColor: 'divider' }}>
            <Box sx={{ py: 1.5, px: 1 }}>
              <List disablePadding>
                <ListItemButton
                  selected={ordersView === 'zelle'}
                  onClick={() => {
                    setOrdersView('zelle');
                    saveOrdersSearchSnapshotToSession(buildOrdersSearchSnapshot({
                      rangePreset,
                      startDate,
                      endDate,
                      q,
                      status,
                      paymentMethods,
                      minTotal,
                      maxTotal,
                      channel,
                      staff,
                      guest,
                      notes,
                      selectedProducts,
                      selectedVariations,
                      page,
                      perPage,
                      sortBy,
                      sortDir,
                      ordersView: 'zelle',
                    }));
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: ordersView === 'zelle' ? 'inherit' : 'action.active' }}>
                    <AccountBalanceIcon />
                  </ListItemIcon>
                  <ListItemText primary="Zelle Payments" secondary="Review kiosk Zelle rows and Woo links" primaryTypographyProps={{ fontWeight: 500 }} />
                  <ChevronRightIcon sx={{ opacity: ordersView === 'zelle' ? 1 : 0.5 }} />
                </ListItemButton>
              </List>
            </Box>
          </Paper>
        </Grid>

        <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
          {ordersView === 'search' ? (
          <Paper variant="outlined">
            <Box sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Results</Typography>
              <Typography variant="body2" color="text.secondary">{meta ? `${meta.total ?? meta.returned ?? orders.length} results` : 'Run a search with the filters'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Sort by <strong>Order #</strong>, <strong>Date</strong>, <strong>Devotee</strong>, <strong>Status</strong>, or <strong>Total</strong> — click the header; click again to reverse. Products lists multiple lines per order, so that column is not sortable.
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Typography variant="body2" color="text.secondary">Searching...</Typography>
              ) : orders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No orders</Typography>
              ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  {/* Top pagination */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Pagination
                      count={Math.max(1, Math.ceil((meta?.total || displayOrders.length) / perPage))}
                      page={page}
                      onChange={(_, val) => { setPage(val); handleSearch({ page: val }); }}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '70px 140px 130px minmax(150px, 1fr) 150px 90px 140px', columnGap: 0, rowGap: 0.75, alignItems: 'center', width: '100%' }}>
                    <Tooltip title="Sort by order number" enterDelay={400}>
                      <TableSortLabel
                        active={sortBy === 'id'}
                        direction={sortBy === 'id' ? sortDir : 'asc'}
                        onClick={() => handleSort('id')}
                        sx={sortHeaderSx('id')}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>Order #</Typography>
                      </TableSortLabel>
                    </Tooltip>
                    <Tooltip title="Sort by date and time" enterDelay={400}>
                      <TableSortLabel
                        active={sortBy === 'date'}
                        direction={sortBy === 'date' ? sortDir : 'asc'}
                        onClick={() => handleSort('date')}
                        sx={sortHeaderSx('date')}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>Date/Time</Typography>
                      </TableSortLabel>
                    </Tooltip>
                    <Tooltip title="Sort by donor name or email" enterDelay={400}>
                      <TableSortLabel
                        active={sortBy === 'devotee'}
                        direction={sortBy === 'devotee' ? sortDir : 'asc'}
                        onClick={() => handleSort('devotee')}
                        sx={sortHeaderSx('devotee')}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>Devotee</Typography>
                      </TableSortLabel>
                    </Tooltip>
                    <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: 0.5, color: 'text.secondary' }}>Products</Typography>
                    <Tooltip title="Sort by order status" enterDelay={400}>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortDir : 'asc'}
                        onClick={() => handleSort('status')}
                        sx={sortHeaderSx('status')}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>Status</Typography>
                      </TableSortLabel>
                    </Tooltip>
                    <Tooltip title="Sort by order total" enterDelay={400}>
                      <TableSortLabel
                        active={sortBy === 'total'}
                        direction={sortBy === 'total' ? sortDir : 'asc'}
                        onClick={() => handleSort('total')}
                        sx={{ ...sortHeaderSx('total'), justifyContent: 'flex-end', width: '100%' }}
                      >
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>Total</Typography>
                      </TableSortLabel>
                    </Tooltip>
                    <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>Actions</Typography>

                  {displayOrders.map((o, rowIdx) => {
                    const devotee = `${o.billing_first_name || ''} ${o.billing_last_name || ''}`.trim() || (o.billing_email || '');
                    const items = Array.isArray(o.items) ? o.items : [];
                    const status = o.status || '';
                    const isPledgeCompleted = status === 'pledge-completed';
                    const isPledgePending = status === 'pledge-pending';
                    
                    // Determine text color for entire row based on pledge status
                    let rowTextColor = 'inherit';
                    if (isPledgeCompleted) {
                      rowTextColor = '#4169E1'; // royal blue
                    } else if (isPledgePending) {
                      rowTextColor = '#800000'; // maroon
                    }
                    
                    const rowBg = rowIdx % 2 === 0 ? '#f5f5f5' : '#ffffff';
                    const cellSx = { backgroundColor: rowBg, px: 0.75, py: 0.75, cursor: 'pointer' };
                    return (
                      <React.Fragment key={o.id}>
                        <Box onClick={() => goToOrderEdit(o)} sx={cellSx}>
                          <Typography variant="body2" noWrap sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem', lineHeight: 1.3, color: rowTextColor }}>{o.id}</Typography>
                        </Box>
                        <Box onClick={() => goToOrderEdit(o)} sx={cellSx}>
                          <Typography variant="body2" noWrap sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem', lineHeight: 1.3, color: rowTextColor }}>{fmt(o.date_created_gmt)}</Typography>
                        </Box>
                        <Box onClick={() => goToOrderEdit(o)} sx={cellSx}>
                          <Typography variant="body2" noWrap sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '0.85rem', lineHeight: 1.3, color: rowTextColor }}>{devotee}</Typography>
                        </Box>
                        <Box onClick={() => goToOrderEdit(o)} sx={{ ...cellSx, overflow: 'hidden' }}>
                          {items.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Stack spacing={0.15}>
                              {items.map((it, idx) => (
                                <Typography key={idx} variant="body2" sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '0.8rem', lineHeight: 1.2, color: rowTextColor }}>
                                  {it.name || `#${it.product_id}`} {it.sku ? `• ${it.sku}` : ''} ×{it.quantity} — ${Number(it.total_num ?? it.price_num ?? 0).toFixed(2)}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Box>
                        <Box onClick={() => goToOrderEdit(o)} sx={cellSx}>
                          <Typography 
                            variant="body2" 
                            noWrap 
                            sx={{ 
                              textTransform: 'capitalize', 
                              fontSize: '0.85rem', 
                              lineHeight: 1.3,
                              color: rowTextColor
                            }}
                          >
                            {status}
                          </Typography>
                        </Box>
                        <Box onClick={() => goToOrderEdit(o)} sx={{ ...cellSx, textAlign: 'right' }}>
                          <Typography variant="body2" noWrap sx={{ textAlign: 'right', fontSize: '0.85rem', lineHeight: 1.3, color: rowTextColor }}>${Number(o.total_num || 0).toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', ...cellSx, cursor: 'default', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                          {(isPledgeCompleted || isPledgePending) && (
                            <Button 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              onClick={async () => {
                                setSelectedOrder(o);
                                setPledgeOrdersOpen(true);
                                setLoadingPledgeOrders(true);
                                setPledgeOrders([]);
                                try {
                                  // Get product_id and variation_id from first item
                                  const firstItem = items.length > 0 ? items[0] : null;
                                  if (!firstItem) {
                                    setPledgeOrders([]);
                                    return;
                                  }
                                  
                                  const params = {
                                    product_id: firstItem.product_id,
                                    pledge_date: o.date_created_gmt || o.date_created
                                  };
                                  
                                  if (firstItem.variation_id) {
                                    params.variation_id = firstItem.variation_id;
                                  }
                                  
                                  // Use customer_id or billing_email
                                  if (o.customer_id) {
                                    params.customer_id = o.customer_id;
                                  } else if (o.billing_email) {
                                    params.billing_email = o.billing_email;
                                  }
                                  
                                  const res = await get('/ldb/orders/pledge-payments', params);
                                  setPledgeOrders(Array.isArray(res?.data) ? res.data : []);
                                } catch (err) {
                                  console.error('Failed to load pledge orders', err);
                                  setPledgeOrders([]);
                                } finally {
                                  setLoadingPledgeOrders(false);
                                }
                              }}
                            >
                              Orders
                            </Button>
                          )}
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => goToOrderEdit(o)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  </Box>
                  {/* Bottom pagination */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Pagination
                      count={Math.max(1, Math.ceil((meta?.total || displayOrders.length) / perPage))}
                      page={page}
                      onChange={(_, val) => { setPage(val); handleSearch({ page: val }); }}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
          ) : (
          <Paper variant="outlined" sx={{ p: 3, minHeight: 400 }} key="zelle-view">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setOrdersView('search');
                  saveOrdersSearchSnapshotToSession(buildOrdersSearchSnapshot({
                    rangePreset,
                    startDate,
                    endDate,
                    q,
                    status,
                    paymentMethods,
                    minTotal,
                    maxTotal,
                    channel,
                    staff,
                    guest,
                    notes,
                    selectedProducts,
                    selectedVariations,
                    page,
                    perPage,
                    sortBy,
                    sortDir,
                    ordersView: 'search',
                  }));
                }}
              >
                Back to order list
              </Button>
            </Stack>
            <ZellePaymentsPage />
          </Paper>
          )}
        </Grid>
      </Grid>

      {/* Read-only Order Details Dialog (opens on row click) */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {!selectedOrder ? (
            <Typography variant="body2" color="text.secondary">No order selected</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 1 }}>
              <Typography variant="body2"><strong>Order #:</strong> {selectedOrder.id}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {fmt(selectedOrder.date_created_gmt)}</Typography>
              <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}><strong>Donor:</strong> {`${selectedOrder.billing_first_name || ''} ${selectedOrder.billing_last_name || ''}`.trim() || selectedOrder.billing_email}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedOrder.status}</Typography>
              <Typography variant="body2" sx={{ textAlign: 'right' }}><strong>Total:</strong> ${Number(selectedOrder.total_num || 0).toFixed(2)}</Typography>
              <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Products</Typography>
                {Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0 ? (
                  <List dense>
                    {selectedOrder.items.map((it, i) => (
                      <ListItemText key={i} primaryTypographyProps={{ variant: 'body2' }} primary={`${it.name || `#${it.product_id}`} ${it.sku ? `• ${it.sku}` : ''} ×${it.quantity} — $${Number(it.total_num ?? it.price_num ?? 0).toFixed(2)}`} />
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No items</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!!selectedOrder && (
            <Button onClick={() => { setViewDialogOpen(false); if (selectedOrder?.id) goToOrderEdit(selectedOrder); }} variant="contained">Modify</Button>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <OrderActionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        order={selectedOrder}
        onActionComplete={() => {
          setDialogOpen(false);
          // Optionally re-run search to refresh the row
        }}
      />

      {/* Pledge Orders Dialog */}
      <Dialog open={pledgeOrdersOpen} onClose={() => setPledgeOrdersOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Orders for Pledge #{selectedOrder?.id}
          {(() => {
            const customerName = selectedOrder?.billing_first_name || selectedOrder?.billing_last_name
              ? `${selectedOrder.billing_first_name || ''} ${selectedOrder.billing_last_name || ''}`.trim()
              : selectedOrder?.billing_email || '';
            return customerName ? <span> ({customerName})</span> : null;
          })()}
          <IconButton
            aria-label="close"
            onClick={() => setPledgeOrdersOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingPledgeOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : pledgeOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No orders found</Typography>
          ) : (
            <Box>
              {/* Balance Summary */}
              {(() => {
                const pledgeAmount = Number(selectedOrder?.total_num || 0);
                const completedOrdersTotal = pledgeOrders
                  .filter(order => order.status === 'completed')
                  .reduce((sum, order) => sum + Number(order.total_num || 0), 0);
                const balance = pledgeAmount - completedOrdersTotal;
                
                return (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 2, rowGap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Pledge Amount</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>${pledgeAmount.toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Payments Made (Completed)</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>${completedOrdersTotal.toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Balance</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: balance > 0 ? 'warning.main' : 'success.main' }}>
                          ${balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
              
              {(() => {
                const firstItem = selectedOrder?.items && selectedOrder.items.length > 0 ? selectedOrder.items[0] : null;
                const productVariationName = firstItem?.name || `Product #${firstItem?.product_id || 'N/A'}`;
                const pledgeDate = selectedOrder?.date_created_gmt || selectedOrder?.date_created;
                let formattedDate = '';
                if (pledgeDate) {
                  try {
                    const dt = new Date(pledgeDate);
                    const month = String(dt.getMonth() + 1).padStart(2, '0');
                    const day = String(dt.getDate()).padStart(2, '0');
                    const year = dt.getFullYear();
                    formattedDate = `${month}/${day}/${year}`;
                  } catch (e) {
                    formattedDate = pledgeDate;
                  }
                }
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Showing {pledgeOrders.length} order(s) for the <strong>{productVariationName}</strong> on or after the pledge date {formattedDate ? `(${formattedDate})` : ''}
                  </Typography>
                );
              })()}
              <Box sx={{ display: 'grid', gridTemplateColumns: '80px 140px 1fr 100px 100px', columnGap: 1, rowGap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Order #</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Date</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Status</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>Total</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</Typography>
                
                {pledgeOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <Typography variant="body2">{order.id}</Typography>
                    <Typography variant="body2">{fmt(order.date_created_gmt)}</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{order.status}</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right' }}>${Number(order.total_num || 0).toFixed(2)}</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setPledgeOrdersOpen(false);
                          goToOrderEdit(order);
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPledgeOrdersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default OrdersSearchPage;
